import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { getRoleBadgeClass, getRoleLabel } from './OrganizationMembersSection'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface UsageMemberStat {
  uid: string
  email: string
  displayName: string
  role: string
  documentCount: number
  documentCountMonth?: number
  formattingThisMonth: number
}

interface UsageStats {
  period: { year: number; month: number }
  totals: {
    documents: number
    documentsThisMonth: number
    formattingThisMonth: number
    botJobs: number
    members: number
  }
  members: UsageMemberStat[]
}

export function UsageDashboardSection() {
  const { user, firebaseUser } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null)
  const [usageLoading, setUsageLoading] = useState(false)
  const [usageError, setUsageError] = useState<string | null>(null)
  // 表示モード: 累積 or 単月（対象月を選択可能）
  const [mode, setMode] = useState<'cumulative' | 'month'>('month')
  const [targetMonth, setTargetMonth] = useState(() => new Date().toISOString().slice(0, 7)) // yyyy-MM

  const fetchUsage = useCallback(async () => {
    if (!firebaseUser) return
    setUsageLoading(true)
    setUsageError(null)
    try {
      const token = await firebaseUser.getIdToken()
      const [y, m] = targetMonth.split('-')
      const res = await fetch(`${API_BASE}/v1/admin/usage?year=${y}&month=${parseInt(m, 10)}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) throw new Error(`利用状況の取得に失敗しました (${res.status})`)
      setUsageStats(await res.json())
    } catch (error) {
      setUsageError(error instanceof Error ? error.message : '利用状況の取得に失敗しました')
    } finally {
      setUsageLoading(false)
    }
  }, [firebaseUser, targetMonth])

  useEffect(() => {
    if (isAdmin && !usageLoading) {
      fetchUsage()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, fetchUsage])

  if (!isAdmin) {
    return (
      <div className="alert info">管理者のみ閲覧できます</div>
    )
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 className="section-title">利用状況</h2>
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-1)' }}>
            {usageStats
              ? `${usageStats.period.year}年${usageStats.period.month}月の組織全体の利用状況`
              : '組織全体の利用状況'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* 累積/単月切替 */}
          <div className="tab-list" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button
              className={`tab-item${mode === 'month' ? ' active' : ''}`}
              onClick={() => setMode('month')}
            >
              単月
            </button>
            <button
              className={`tab-item${mode === 'cumulative' ? ' active' : ''}`}
              onClick={() => setMode('cumulative')}
            >
              累積
            </button>
          </div>
          {mode === 'month' && (
            <input
              type="month"
              className="form-input"
              style={{ width: 'auto' }}
              value={targetMonth}
              onChange={(e) => e.target.value && setTargetMonth(e.target.value)}
            />
          )}
          <button onClick={fetchUsage} className="btn btn-outline" disabled={usageLoading}>
            更新
          </button>
        </div>
      </div>

      {usageLoading ? (
        <div className="card text-center" style={{ padding: 'var(--space-12)' }}>
          <span className="spinner lg" style={{ margin: '0 auto' }} />
          <p className="text-sm text-secondary" style={{ marginTop: 'var(--space-3)' }}>読み込み中...</p>
        </div>
      ) : usageError ? (
        <div className="card text-center" style={{ padding: 'var(--space-12)' }}>
          <p className="text-sm text-danger">{usageError}</p>
        </div>
      ) : usageStats ? (
        <div>
          {/* KPI */}
          <div className="kpi-grid" style={{ marginBottom: 'var(--space-8)' }}>
            {mode === 'cumulative' ? (
              <>
                <div className="kpi-card">
                  <div className="kpi-label">総ドキュメント数</div>
                  <div className="kpi-value tabular-nums">{usageStats.totals.documents}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">bot会議数（累積）</div>
                  <div className="kpi-value tabular-nums">{usageStats.totals.botJobs}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">メンバー数</div>
                  <div className="kpi-value tabular-nums">{usageStats.totals.members}</div>
                </div>
              </>
            ) : (
              <>
                <div className="kpi-card">
                  <div className="kpi-label">ドキュメント作成数（{usageStats.period.month}月）</div>
                  <div className="kpi-value tabular-nums">{usageStats.totals.documentsThisMonth}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">AI整形回数（{usageStats.period.month}月）</div>
                  <div className="kpi-value tabular-nums">{usageStats.totals.formattingThisMonth}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">メンバー数</div>
                  <div className="kpi-value tabular-nums">{usageStats.totals.members}</div>
                </div>
              </>
            )}
          </div>

          {/* メンバー別利用状況 */}
          <h3 className="text-base font-semibold text-primary" style={{ marginBottom: 'var(--space-4)' }}>メンバー別利用状況</h3>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>メンバー</th>
                  <th>ロール</th>
                  <th>{mode === 'cumulative' ? 'ドキュメント数（累積）' : `ドキュメント数（${usageStats.period.month}月）`}</th>
                  <th>{mode === 'cumulative' ? '—' : `AI整形（${usageStats.period.month}月）`}</th>
                </tr>
              </thead>
              <tbody>
                {usageStats.members.map((m) => (
                  <tr key={m.uid}>
                    <td>
                      <div className="contact-cell">
                        <span className="avatar avatar-md avatar-accent">
                          {m.displayName?.charAt(0) || m.email.charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <span className="contact-name">{m.displayName || 'ユーザー'}</span>
                          <span className="text-sm text-secondary">{m.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={getRoleBadgeClass(m.role)}>
                        {getRoleLabel(m.role)}
                      </span>
                    </td>
                    <td className="tabular-nums">{mode === 'cumulative' ? m.documentCount : (m.documentCountMonth ?? 0)}</td>
                    <td className="tabular-nums">{mode === 'cumulative' ? '—' : m.formattingThisMonth}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="card text-center" style={{ padding: 'var(--space-12)' }}>
          <p className="text-sm text-secondary">利用状況データがありません</p>
        </div>
      )}
    </div>
  )
}
